import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PromiseStatuses } from "../../utils";
import { GetAllNotificationDTO, GetAllNotificationFiltersDTO, NotificationDTO, NotificationsSianCreationDTO, NotificationsSianDTO } from "./dataservice/dto";
import { NewNotificationService } from "./dataservice/service";

interface NotificationState {
    notificationsSianCreationRequest: NotificationsSianCreationDTO
    loadFromXlsFile: File | null
    notificationIdToUpdate: string

    // FILTERS
    getAllNotificationFilters: GetAllNotificationFiltersDTO

    // STATUS
    loadNotificationStatus: PromiseStatuses
    getAllNotificationStatus: PromiseStatuses
    getNotificationByIdStatus: PromiseStatuses

    // RESPONSE
    loadNotificationResponse?: NotificationsSianDTO
    getAllNotificationResponse?: GetAllNotificationDTO
    getNotificationByIdResponse?: NotificationDTO
}

const initialState: NotificationState = {
    notificationIdToUpdate: '',
    notificationsSianCreationRequest: {},
    loadFromXlsFile: null,

    getAllNotificationFilters: {
        itemsPerPage: 0,
        page: 0
    },

    loadNotificationStatus: 'idle',
    getAllNotificationStatus: 'idle',
    getNotificationByIdStatus: 'idle'
}

export const LoadNotificationsFromFile = createAsyncThunk(
    'notification/Load',
    async (data: FormData, thunkApi): Promise<NotificationsSianDTO> => {
        const notificationService = NewNotificationService()

        return notificationService.LoadNotificationsFromFile(data).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GetAllNotification = createAsyncThunk(
    'notification/GetAll',
    async (filters: GetAllNotificationFiltersDTO, thunkApi): Promise<GetAllNotificationDTO> => {
        const notificationService = NewNotificationService()

        return notificationService.GetAllNotification(filters).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GetNotificationById = createAsyncThunk(
    'notification/GetById',
    async (id: string, thunkApi): Promise<NotificationDTO> => {
        const notificationService = NewNotificationService()

        return notificationService.GetNotificationById(id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

const NotificationSlice = createSlice({
    name: 'notification/slice',
    initialState,
    reducers: {
        setNotificationIdToUpdate: (state, action) => {
            state.notificationIdToUpdate = action.payload
        },

        // REQUEST
        setNotificationSianCreationSianType: (state, action) => {
            state.notificationsSianCreationRequest.sianType = action.payload
        },
        setLoadFromXlsFile: (state, action) => {
            state.loadFromXlsFile = action.payload
        },

        // FILTERS
        setGetAllNotificationFiltersItemsPerPage: (state, action) => {
            state.getAllNotificationFilters.itemsPerPage = action.payload
        },
        setGetAllNotificationFiltersPage: (state, action) => {
            state.getAllNotificationFilters.page = action.payload
        },
        setGetAllNotificationFiltersOrder: (state, action) => {
            state.getAllNotificationFilters.order = action.payload
        },
        setGetAllNotificationFiltersSort: (state, action) => {
            state.getAllNotificationFilters.sort = action.payload
        },
        setGetAllNotificationFiltersCustomerId: (state, action) => {
            state.getAllNotificationFilters.customerId = action.payload
        },
        resetGetAllNotificationFilters: (state) => {
            state.getAllNotificationFilters = {
                itemsPerPage: 0,
                page: 0
            }
        },

        // STATUS
        resetLoadNotificationStatus: (state) => {
            state.loadNotificationStatus = 'idle'
        },
        resetGetAllNotificationStatus: (state) => {
            state.getAllNotificationStatus = 'idle'
        },
        resetGetNotificationById: (state) => {
            state.getNotificationByIdStatus = 'idle'
        },

        // RESPONSE
        resetLoadNotificationResponse: (state) => {
            state.loadNotificationResponse = undefined
        }
    },
    extraReducers(builder) {
        builder

            // LOAD
            .addCase(LoadNotificationsFromFile.pending, (state) => {
                state.loadNotificationStatus = 'loading'
            })
            .addCase(LoadNotificationsFromFile.fulfilled, (state, action) => {
                state.loadNotificationStatus = 'successfully'
                state.loadNotificationResponse = action.payload
            })
            .addCase(LoadNotificationsFromFile.rejected, (state) => {
                state.loadNotificationStatus = 'failed'
            })

            // GET ALL
            .addCase(GetAllNotification.pending, (state) => {
                state.getAllNotificationStatus = 'loading'
            })
            .addCase(GetAllNotification.fulfilled, (state, action) => {
                state.getAllNotificationStatus = 'successfully'
                state.getAllNotificationResponse = action.payload
            })
            .addCase(GetAllNotification.rejected, (state) => {
                state.getAllNotificationStatus = 'failed'
            })

            // GET BY ID
            .addCase(GetNotificationById.pending, (state) => {
                state.getNotificationByIdStatus = 'loading'
            })
            .addCase(GetNotificationById.fulfilled, (state, action) => {
                state.getNotificationByIdStatus = 'successfully'
                state.getNotificationByIdResponse = action.payload
            })
            .addCase(GetNotificationById.rejected, (state) => {
                state.getNotificationByIdStatus = 'failed'
            })
    },
})

export const {
    setNotificationIdToUpdate,
    setLoadFromXlsFile,
    setNotificationSianCreationSianType,
    setGetAllNotificationFiltersCustomerId,
    setGetAllNotificationFiltersItemsPerPage,
    setGetAllNotificationFiltersOrder,
    setGetAllNotificationFiltersPage,
    setGetAllNotificationFiltersSort,
    resetGetAllNotificationFilters,
    resetGetAllNotificationStatus,
    resetGetNotificationById,
    resetLoadNotificationResponse,
    resetLoadNotificationStatus
} = NotificationSlice.actions

export default NotificationSlice.reducer